/**
 * @module Components.Routes
 *
 */
import React, { useState, useEffect, useContext } from "react";
import { DateTime } from "luxon";
import { useForm, FormProvider } from "react-hook-form";

import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";

import { EventModel, EventType } from "app/models/EventModel";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";

import EventTypeSelector from "app/routes/Calendar/EventForm/EventTypeSelector";
import BillNowConfirmationModal from "app/routes/Calendar/EventForm/BillNowConfirmationModal";
import { needsBillNowConfirmation } from "app/routes/Calendar/EventForm/paymentHelpers";
import { sessionCountForRecurrence } from "app/routes/Calendar/EventForm/recurrenceCount";
import SessionForm from "app/routes/Calendar/EventForm/SessionForm";
import ScheduleBlockForm from "app/routes/Calendar/EventForm/ScheduleBlockForm";
import {
  BlockOwnerMode,
  blockOwnerFields,
  blocksResources,
  blocksResourcesOnly,
  canBlockResources,
  initialBlockOwner,
} from "app/routes/Calendar/EventForm/blockOwner";

import CloseIcon from "app/images/close.svg";
import { useTranslation } from "react-i18next";
import { FacilityModel } from "app/models/scheduling/FacilityModel";
import { RoleType, UserContext } from "app/contexts/UserContext";

import styles from "app/routes/Calendar/EventForm/styles.module.scss";
import { useActions } from "app/utils/hooks";
import {
  clearAppliedTemplates,
  createSpace,
  getCreatedSpace,
} from "app/actions/spaceCreation";
import ButtonVariant from "app/components/Button/ButtonVariant";

type PropsDef = {
  event: EventModel;
  coachProfileOptions?: CoachProfileModel[];
  onCancel: () => void;
  onSave: (event: EventModel) => void;
  submitting: boolean;
  errors: any;
  coachProfiles: CoachProfileModel[];
  allowCoachSelection?: boolean;
  facility?: FacilityModel;
  facilityLessonRates?: LessonRateModel[];
  facilityBookingEnabled?: boolean;
  // True on the Resource Calendar, the view with a column for each bay.
  bayView?: boolean;
};

export default function EventForm({
  event,
  onCancel,
  coachProfileOptions,
  onSave,
  errors,
  submitting = false,
  coachProfiles,
  allowCoachSelection = false,
  facility,
  facilityLessonRates,
  facilityBookingEnabled,
  bayView = false,
}: PropsDef) {
  const { t } = useTranslation();
  const user = useContext(UserContext);

  // An admin, a coach-admin or a scheduler can block a bay on the Resource
  // Calendar while the facility has the feature on. A scheduler blocks bays
  // only, so a scheduler gets no Unavailability on the Coaches view. A coach
  // blocks coaches only.
  const allowResourceBlock = canBlockResources(user, facility, bayView);
  const resourcesOnly = allowResourceBlock && blocksResourcesOnly(user);

  const methods = useForm({
    defaultValues: {
      ...event,
      coachProfiles,
      // A click on a bay, or the edit or the copy of a bay block, starts on
      // "Resources" with that bay (DD-18).
      ...initialBlockOwner(event, allowResourceBlock, resourcesOnly),
    },
    mode: "onChange",
  });

  const {
    control,
    register,
    unregister,
    handleSubmit,
    setValue,
    watch,
    formState,
    clearErrors,
    setError,
    getValues,
    resetField,
  } = methods;

  const [availablePackages, setAvailablePackages] = useState([]);

  useEffect(() => {
    clearErrors();

    if (!errors) {
      return;
    }

    if (Object.keys(errors).length > 0) {
      Object.keys(errors).forEach((key) => {
        const error = errors[key];
        if (error) {
          const field = key === "studentProfileIds" ? "studentProfiles" : key;
          const message = error.join(", ");
          // @ts-expect-error - setError field type mismatch
          setError(field, { type: "custom", message });
        }
      });
    }
  }, [errors]);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingBillNowData, setPendingBillNowData] = useState<any>(null);

  const fadeForModal = showConfirmation || !!pendingBillNowData;

  const watchEventType = watch("type");
  const watchCoaches = watch("coachProfiles");
  const watchStart = watch("start");
  const watchEnd = watch("end");
  const watchTimeZone = watch("timeZone");

  // A session must start and end on the same calendar day (a long booking option
  // or late start would push the end past midnight, which the calendar can't
  // represent). Multi-day blocks are exempt.
  const crossesMidnight = React.useMemo(() => {
    if (watchEventType === EventType.COACH_SCHEDULE_BLOCK || !watchStart || !watchEnd) {
      return false;
    }
    let start = DateTime.fromISO(watchStart);
    let end = DateTime.fromISO(watchEnd);
    if (watchTimeZone) {
      start = start.setZone(watchTimeZone);
      end = end.setZone(watchTimeZone);
    }
    if (!start.isValid || !end.isValid) {
      return false;
    }
    // Mirror the save-time normalization: the end time-of-day is stamped onto the
    // start's day, and a duration whose end is at/before the start wraps to the
    // next day. Evaluating the guard against that same normalized end catches a
    // wrapped range (end time-of-day <= start, e.g. 10:00→09:00) — not just an
    // end already dated past midnight.
    let normalizedEnd = end.set({ year: start.year, month: start.month, day: start.day });
    if (normalizedEnd <= start) {
      normalizedEnd = normalizedEnd.plus({ days: 1 });
    }
    // Allowed up to and including the midnight that closes the start day (a full
    // 00:00→00:00 day, or a session ending exactly at midnight). Only forbidden
    // when the end runs past that midnight into the next day.
    const nextMidnight = start.startOf("day").plus({ days: 1 });
    return normalizedEnd > nextMidnight;
  }, [watchEventType, watchStart, watchEnd, watchTimeZone]);

  const isEdit = !!event.id;
  const isNew = !isEdit;

  const isAdmin =
    user.role === RoleType.ADMIN ||
    (user.role === RoleType.COACH && user.isAdmin);
  const canEditCoachOnExistingLesson =
    isEdit &&
    watchEventType === EventType.LESSON_SCHEDULE &&
    facility?.featureFlags?.bookingOptionsCapacity === true &&
    (user.role === RoleType.ADMIN || user.behavesLikeAdmin);

  const showCoachSelection =
    canEditCoachOnExistingLesson ||
    (isNew &&
      (allowCoachSelection ||
        (watchEventType === EventType.COACH_SCHEDULE_BLOCK && isAdmin)));
  const ownEvent = coachProfiles.map((c) => c?.id).includes(user.id);

  const onCoachChange = (changeEvent: any) => {
    const coaches = changeEvent.target.value;
    setValue("coachProfiles", coaches, { shouldDirty: true });

    if (!isEdit) {
      const selectedDate = getValues().date;
      if (
        selectedDate < coaches[0].earliestBookingDate &&
        !isAdmin &&
        !ownEvent
      ) {
        setValue("date", coaches[0].earliestBookingDate);
      }
      resetField("lessonRateId");
      resetField("rate");
      resetField("location");
      resetField("private");
      resetField("start");
      resetField("end");
      // The bay goes back to its start value too: the bay of the click, or
      // "Auto" after the New Event button.
      resetField("scheduleResourceId");
    }
  };

  const onDateChange = (changeEvent: any) => {
    const date = changeEvent.target.value;

    setValue("date", date, { shouldValidate: true, shouldDirty: true });
    const startTime = DateTime.fromISO(getValues("start"))
      .setZone(event.timeZone)
      .set({
        year: date.year,
        month: date.month,
        day: date.day,
      });
    let endTime = DateTime.fromISO(getValues("end"))
      .setZone(event.timeZone)
      .set({
        year: date.year,
        month: date.month,
        day: date.day,
      });
    // A duration that wraps past midnight (end time-of-day <= start) belongs on
    // the next day; keep it there rather than collapsing to a zero-length range.
    if (endTime <= startTime) {
      endTime = endTime.plus({ days: 1 });
    }

    setValue("start", startTime.toISO(), { shouldDirty: true });
    setValue("end", endTime.toISO(), { shouldDirty: true });
  };

  const createSpaceActions = useActions({
    createSpace,
    getCreatedSpace,
    clearAppliedTemplates,
  });

  const registerNewStudents = (data: any) => {
    const newStudents = data.studentProfiles
      .filter((student) => student.newStudent === true)
      .map((student) => {
        return {
          email: student.email,
          name: student.fullName,
          firstName: student.firstName,
          lastName: student.lastName,
          user_id: student.id,
          templateId: student.templateId,
          newSpace: student.newSpace,
        };
      });

    const coaches = data.coachProfiles?.map((coach) => {
      return {
        email: coach.email,
        name: coach.fullName,
        user_id: coach.id,
      };
    });

    newStudents?.map((student: any) => {
      const { templateId, newSpace, ...newStudent } = student;
      if (newSpace === true) {
        const isSelf = coaches?.some((coach) => coach.user_id === user.id);
        const newBaseApplication = {
          athletes: [newStudent],
          coaches,
          create_for_self: false,
          add_self_as_member: isSelf,
          segment_ids: [],
          space_template_id: templateId,
          sport: "Golf",
          sport_other: "Golf",
        };

        createSpaceActions.createSpace({
          spaces_import: newBaseApplication,
        } as any);
      }
    });
  };

  const onSubmit = (data: any) => {
    // Also block here (not only via the disabled Save button) so implicit submit
    // — e.g. pressing Enter — can't bypass the same-day rule.
    if (crossesMidnight) {
      return;
    }

    if (needsBillNowConfirmation(data, isNew)) {
      setPendingBillNowData(data);
      return;
    }

    submitEvent(data);
  };

  const submitEvent = (data: any) => {
    if (data.studentProfiles) {
      registerNewStudents(data);
    }

    let eventData: any;
    if (data.type === "lesson_schedule") {
      let selectedPackage;
      if (data.payment_method === "package" && data.selected_package_id) {
        const selectedProduct = availablePackages.find(
          (product) => product.rateLinkId === Number(data.selected_package_id),
        );
        if (selectedProduct) {
          selectedPackage = selectedProduct.rateLinkId;
        }
      }

      eventData = {
        ...data,
        studentProfileIds: data.studentProfiles.map(
          (student: any) => student.id,
        ),
        coachProfileIds: [data.coachProfiles[0].id],
        rate: !data.rate ? 0 : data.rate,
        payment_method: data.payment_method,
        charge_timing: data.charge_timing,
        stripeProductRateLinkId: selectedPackage,
        payingAthleteId: data.paying_athlete_id?.external_id,
      };
      delete eventData.studentProfiles;
      delete eventData.coachProfiles;
    } else {
      // Resources: one block for each bay, with no coach. Coaches: the
      // coaches of the select, with no bay.
      eventData = {
        ...data,
        ...blockOwnerFields(data, allowResourceBlock),
      };
    }

    const stampedStart = DateTime.fromISO(eventData.start)
      .setZone(eventData.timeZone)
      .set({
        year: eventData.date.year,
        month: eventData.date.month,
        day: eventData.date.day,
      });
    let stampedEnd = DateTime.fromISO(eventData.end)
      .setZone(eventData.timeZone)
      .set({
        year: eventData.date.year,
        month: eventData.date.month,
        day: eventData.date.day,
      });
    // A duration that wraps past midnight (end time-of-day <= start) belongs on
    // the next day; keep it there rather than collapsing to a zero-length range.
    if (stampedEnd <= stampedStart) {
      stampedEnd = stampedEnd.plus({ days: 1 });
    }

    eventData = {
      ...eventData,
      start: stampedStart.toISO(),
      end: stampedEnd.toISO(),
    };
    onSave(eventData);
  };

  const title = isEdit ? t("Edit Event") : t("New Event");

  const onEventTypeChange = (changeEvent: any) => {
    const eventType = changeEvent.target.value;
    if (eventType === "lesson_schedule") {
      resetField("coachProfiles");
      unregister("title");
      unregister("private");
    }

    if (eventType === "coach_schedule_block") {
      unregister("studentProfiles", { keepDefaultValue: true });
      unregister("lessonRateId", { keepDefaultValue: true });
      unregister("rate", { keepDefaultValue: true });
      unregister("location", { keepDefaultValue: true });
    }

    setValue("type", eventType);
  };

  const onDismiss = () => {
    const hasDirtyFields = Object.keys(formState.dirtyFields).length > 0;
    if (hasDirtyFields) {
      setShowConfirmation(true);
    } else {
      onCancel();
    }
  };

  const loadCoaches = async (
    _inputValue: string,
    _currentOptions: [],
    _additional: { page: number },
  ) => {
    return {
      options: coachProfileOptions || [],
      hasMore: false,
    };
  };

  const validateCoachSelection = (
    coachSelection: CoachProfileModel[],
    formValues: any,
  ) => {
    if (formValues.type === EventType.LESSON_SCHEDULE) {
      return coachSelection.length === 1;
    }
    // A block for bays has no coach.
    if (blocksResources(formValues, allowResourceBlock)) {
      return true;
    }

    return coachSelection.length > 0;
  };

  // A block for bays needs one bay or more. The rule does not apply to a
  // block for coaches or to a session.
  const validateResourceSelection = (ids: number[], formValues: any) =>
    !blocksResources(formValues, allowResourceBlock) || (ids?.length ?? 0) > 0;

  // The owner change also checks the form again, so the Save button follows
  // the rule of the new owner.
  const onAppliesToChange = (mode: BlockOwnerMode) => {
    setValue("appliesTo", mode, { shouldDirty: true, shouldValidate: true });
  };

  // A scheduler offers "Unavailability" for bays only.
  const allowScheduleBlock = ownEvent || isAdmin || resourcesOnly;

  return (
    <>
      <ModalLayout
        isOpen
        classNames={`${styles.modal} ${fadeForModal ? styles.fade_for_confirmation : ""}  event_form_container`}
      >
        <div className={styles.header}>
          <h2>{title}</h2>
          <button
            type="button"
            className={styles.close_button}
            onClick={onDismiss}
            aria-label="Close"
          >
            <img src={CloseIcon} alt="Close" />
          </button>
        </div>

        <div className={`${styles.event_form}`}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              {isNew && (
                <EventTypeSelector
                  {...register("type", { onChange: onEventTypeChange })}
                  control={control}
                  allowUnavailable={allowScheduleBlock}
                />
              )}

              {watchEventType === "coach_schedule_block" ? (
                <ScheduleBlockForm
                  onDateChange={onDateChange}
                  showCoachSelection={showCoachSelection}
                  onCoachChange={onCoachChange}
                  loadCoaches={loadCoaches}
                  validateCoachSelection={validateCoachSelection}
                  allowResourceBlock={allowResourceBlock}
                  lockOwner={isEdit}
                  resourcesOnly={resourcesOnly}
                  facilityId={facility?.id}
                  heldResources={event.scheduleResources}
                  validateResourceSelection={validateResourceSelection}
                  onAppliesToChange={onAppliesToChange}
                />
              ) : (
                <SessionForm
                  lessonRateOptions={
                    facilityBookingEnabled && facilityLessonRates
                      ? facilityLessonRates
                      : [...(watchCoaches[0]?.lessonRates || [])].sort((a, b) => {
                          const orderA = a.orderNumber ?? Number.MAX_SAFE_INTEGER;
                          const orderB = b.orderNumber ?? Number.MAX_SAFE_INTEGER;
                          return orderA - orderB;
                        })
                  }
                  onDateChange={onDateChange}
                  coachProfile={watchCoaches[0]}
                  facility={facility}
                  canAddNewAthletes={!!watchCoaches[0]?.id}
                  coachId={watchCoaches[0]?.id}
                  facilityId={facility?.id}
                  setAvailablePackages={setAvailablePackages}
                  availablePackages={availablePackages}
                  isEdit={isEdit}
                  event={event}
                  facilityBookingEnabled={facilityBookingEnabled}
                  showCoachSelection={showCoachSelection}
                  onCoachChange={onCoachChange}
                  loadCoaches={loadCoaches}
                  validateCoachSelection={validateCoachSelection}
                  watchEventType={watchEventType}
                />
              )}
            </form>
          </FormProvider>
        </div>

        {crossesMidnight && (
          <div className={styles.cross_day_warning}>
            {t("A session must start and end on the same day. Please pick a shorter duration or an earlier start time.")}
          </div>
        )}

        <div className={styles.form_actions}>
          <ButtonVariant
            title={t("Cancel")}
            buttonType="secondary-outline"
            onClickFunc={onDismiss}
          />
          <ButtonVariant
            disabled={!formState.isValid || submitting || crossesMidnight}
            title={submitting ? t("Saving...") : t("Save")}
            buttonType="primary"
            onClickFunc={handleSubmit(onSubmit)}
          />
        </div>
      </ModalLayout>

      <ModalLayout
        isOpen={showConfirmation}
        classNames={styles.confirm_cancel_modal}
        wrapperClassName="confirm_cancel"
      >
        <div className={styles.confirmation_modal_content}>
          <div>{t("Are you sure you want to discard this event?")}</div>
          <div className={styles.confirmation_actions}>
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setShowConfirmation(false)}
            >
              {t("Cancel")}
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                setShowConfirmation(false);
                onCancel();
              }}
            >
              {t("Discard")}
            </button>
          </div>
        </div>
      </ModalLayout>

      <BillNowConfirmationModal
        isOpen={!!pendingBillNowData}
        onlinePaymentType={pendingBillNowData?.online_payment_type}
        chargeTiming={pendingBillNowData?.charge_timing}
        currencySymbol={watchCoaches?.[0]?.currencySymbol}
        sessionPrice={Number(pendingBillNowData?.rate) || 0}
        sessionCount={sessionCountForRecurrence(
          pendingBillNowData?.repeat,
          pendingBillNowData?.date,
          pendingBillNowData?.untilDate,
        )}
        onCancel={() => setPendingBillNowData(null)}
        onConfirm={() => {
          const data = pendingBillNowData;
          setPendingBillNowData(null);
          submitEvent(data);
        }}
      />
    </>
  );
}
